import React, { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Item, useCanvasStore } from '@/stores/canvasStore';
import { useDragToBoardStore } from '@/stores/dragToBoardStore';
import { getFolderColorFromValue } from '@/lib/folderColors';
import { useHistoryStore } from '@/stores/historyStore';
import { boardContents, dropPosition, folderSublabel, paperLabel, FOLDER_PREVIEW_MAX } from '@/lib/folderPreview';
import { FolderFloat, type FolderDrop, type FolderFloatEntry } from './folder/FolderFloat';
import { FolderPaper } from './folder/FolderPaper';

interface BoardCardProps {
  item: Item;
  isSelected?: boolean;
  /** Keep the folder shut: the card is being dragged, resized, renamed, or the canvas is panning. */
  suppressed?: boolean;
  /** The folder fanned out or closed; the canvas lifts the card above its neighbours while open. */
  onOpenChange?: (open: boolean) => void;
}

export function BoardCard({ item, isSelected = false, suppressed = false, onOpenChange }: BoardCardProps) {
  const content = item.content as {
    linked_board_id?: string;
    name?: string;
    color?: string;
  };
  const boardId = content.linked_board_id;

  // Newest items in the folder. Shallow-compared, so dragging something on
  // the current board doesn't re-render every folder on screen.
  const previews = useCanvasStore(
    useShallow((s) => boardContents(s.allItems, boardId).slice(0, FOLDER_PREVIEW_MAX)),
  );
  const count = useCanvasStore((s) => boardContents(s.allItems, boardId).length);
  const allItemsLoaded = useCanvasStore((s) => s.allItems.length > 0);
  const openItemOnCanvas = useCanvasStore((s) => s.openItemOnCanvas);
  const pullItemToCurrentBoard = useCanvasStore((s) => s.pullItemToCurrentBoard);

  const { hoverBoardId, hoverPhase, hoverProgress, isDragging } = useDragToBoardStore();
  const isHovered = isDragging && hoverBoardId === boardId;
  const isHolding = isHovered && (hoverPhase === 'holding' || hoverPhase === 'opening');
  const isOpening = isHovered && hoverPhase === 'opening';

  const palette = getFolderColorFromValue(content.color);
  const scale = item.width / 200;

  const entries = useMemo<FolderFloatEntry[]>(
    () =>
      previews.map((p) => ({
        key: p.id,
        label: paperLabel(p),
        content: <FolderPaper item={p} scale={scale} textColor="hsl(var(--ink))" />,
      })),
    [previews, scale],
  );

  const handleSelect = useCallback(
    (key: string) => {
      const picked = previews.find((p) => p.id === key);
      if (picked) void openItemOnCanvas(picked);
    },
    [previews, openItemOnCanvas],
  );

  // A paper dragged out of the folder lands on this canvas where it was dropped
  const handleDragOut = useCallback(
    async (key: string, _index: number, drop: FolderDrop) => {
      const target = document.elementFromPoint(drop.clientX, drop.clientY);
      // Only onto the canvas itself: not toolbars, panels or menus over it
      if (!target?.closest('[data-canvas-export-target]')) return false;
      const picked = previews.find((p) => p.id === key);
      if (!picked) return false;

      const position = dropPosition(item, drop.folderRect, drop, picked);
      const moved = await pullItemToCurrentBoard(picked.id, position);
      if (!moved) return false;

      useHistoryStore.getState().pushAction({
        type: 'MOVE_TO_BOARD',
        timestamp: Date.now(),
        itemIds: [picked.id],
        previousBoardId: picked.board_id,
        newBoardId: moved.board_id,
        previousPositions: new Map([[picked.id, { x: picked.position_x, y: picked.position_y }]]),
        newPositions: new Map([[picked.id, { x: moved.position_x, y: moved.position_y }]]),
      });
      return true;
    },
    [item, previews, pullItemToCurrentBoard],
  );

  return (
    <div
      className="relative h-full w-full transition-transform duration-200 ease-out motion-reduce:transition-none"
      style={{
        transform: `scale(${isOpening ? 1.08 : isHovered ? 1.04 : 1})`,
        transformOrigin: 'center center',
      }}
    >
      <FolderFloat
        items={entries}
        label={content.name || 'Untitled'}
        sublabel={allItemsLoaded ? folderSublabel(count) : undefined}
        trigger="hover"
        closeOnSelect
        physics
        drift={0.5}
        onSelect={handleSelect}
        onDragOut={handleDragOut}
        onOpenChange={onOpenChange}
        disabled={suppressed || isDragging}
        flapOpen={isHovered}
        folderColor={palette.folderDark}
        frontColor={palette.folder}
        paperColor={palette.label}
        itemColor="hsl(var(--paper-raised))"
        itemTextColor="hsl(var(--ink))"
        labelColor={palette.labelText}
        outlineColor={isSelected ? 'hsl(var(--signal))' : null}
        width={item.width}
        height={item.height}
        radius={14 * scale}
        spread={180 * scale}
        lift={26 * scale}
        tilt={8}
        flapAngle={34}
        restAngle={16}
        openDuration={520}
        stagger={45}
        bounce={0.3}
        openDelay={120}
        reopenDelay={450}
        closeDelay={140}
      >
        {/* Hold-to-open progress while something is dragged over the folder */}
        {isHolding && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ paddingTop: '22%' }}>
            <div className="relative h-12 w-12">
              <svg className="h-full w-full -rotate-90" viewBox="0 0 48 48" aria-hidden="true">
                <circle cx="24" cy="24" r="20" fill="none" stroke={palette.labelText} strokeWidth="3" opacity="0.2" />
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  fill="none"
                  stroke={palette.labelText}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={`${(hoverProgress / 100) * 125.66} 125.66`}
                  className="transition-all duration-100"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold" style={{ color: palette.labelText }}>
                  {Math.round(hoverProgress)}%
                </span>
              </div>
            </div>
          </div>
        )}

        {isHovered && hoverPhase === 'hovering' && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ paddingTop: '22%' }}>
            <span
              className="rounded-full px-2.5 py-1 text-xs font-medium"
              style={{ background: palette.label, color: palette.labelText }}
            >
              Release to drop
            </span>
          </div>
        )}
      </FolderFloat>
    </div>
  );
}
